<template>
  <div :class="[baseCss, 'select-none', disabled ? 'pointer-events-none' : null, block ? ['block', 'w-full'] : ['inline-block', 'align-middle', 'select-none'], 'outline-none']" :style="{width}">
    <table-layout>
      <row>
        <cell>
          <label :class="['block', 'cursor-pointer', 'outline-none']" :for="id" v-ripple>
            <btn :icon-left="iconLeft" :icon-right="iconRight" :flat="flat" :rounded="rounded" :plain="plain" :fancy="fancy" :size="size" :elevation="elevation" :disabled="disabled" :block="block" :icon-provider="iconProvider" :color="color" :loading="loading" :color-lightness="colorLightness" :bg-color="bgColor" :bg-color-lightness="bgColorLightness" :class="['pointer-events-none', 'outline-none']">{{ label }}</btn>
            <form>
              <input type="file" @change="getFileData" :id="id" :accept="mimeTypes.join(',')" :files="[]" :disabled="disabled" :multiple="multiple ? 'multiple' : null" tabindex="-1" />
            </form>
          </label>
        </cell>
        <cell v-if="preview && src && !multiple" style="width: 32px" :class="['pl-1']">
          <btn @click="reset" :class="['btn-reset']" :icon-left="iconRemoveFile" :rounded="rounded" :elevation="elevation" :flat="flat" :plain="plain" :size="size" :icon-provider="iconProvider" :color="color" :color-lightness="colorLightness" :bg-color="bgColor" :bg-color-lightness="bgColorLightness" />
        </cell>
      </row>
    </table-layout>
    <card v-if="preview" :elevation="elevation" :rounded="rounded" :overflow-off="!!previewMaxHeight" :class="[previewMaxHeight ? 'overflow-y-auto' : null, 'mt-1']" :style="{maxHeight: previewMaxHeight}">
      <template v-if="!multiple">
        <card-text v-if="src" padding-off :class="['select-none', 'relative', `${baseCss}-single-preview`]">
          <container v-if="isImage(0) || isText(0)">
            <container :class="['p-1', 'absolute', 'bottom-0', 'w-full', 'text-white', `${baseCss}-single-preview-details`, 'text-xs']">
              <div v-html="files.item(0).name" />
              <div v-html="files.item(0).sizeFormatted" />
            </container>
            <img v-if="isImage(0)" :src="files.item(0).result" :class="['block', 'w-full']" />
            <pre v-else-if="isText(0)" v-text="files.item(0).result" :class="['text-sm', 'p-2']" />
          </container>
          <alert v-else type="warning" :dismissible="false" :class="['m-0.5']" v-model="previewNotAvailable">{{ messagePreviewNotAvailable }}</alert>
        </card-text>
        <card-text v-if="fileTooBig">
          <alert :dismissible="false" v-model="fileTooBig" type="error" :class="['m-0.5']">{{ messageFileTooBig }}</alert>
        </card-text>
        <card-text v-if="fileNotAllowed">
          <alert :dismissible="false" v-model="fileNotAllowed" type="error" :class="['m-0.5']">{{ messageFileNotAllowed }}</alert>
        </card-text>
      </template>
      <template v-if="multiple && files.length">
        <list :clickable="false">
          <list-item v-for="(file, index) in files" :key="index">
            <list-item-avatar>
              <avatar bg-color="transparent" :bg-color-lightness="null" :tile="true" v-if="isImage(index)" :src="file.result" />
              <icon name="file" v-else :color="bgColor" :color-lightness="bgColorLightness" />
            </list-item-avatar>
            <list-item-content>
              <list-item-content-title v-html="file.name" />
              <list-item-content-sub-title :class="[file.fileTooBig ? ['text-red-500', 'font-bold'] : null]" v-html="file.fileTooBig ? messageFileTooBig : file.sizeFormatted" />
            </list-item-content>
            <list-item-icon>
              <icon :class="['cursor-pointer']" @click="removeFile(index)" :name="iconRemoveFile" :color="bgColor" :color-lightness="bgColorLightness" />
            </list-item-icon>
          </list-item>
        </list>
      </template>
    </card>
  </div>
</template>
<script>
import methods from '../../mixins/methods'

export default {
  name: 'Upload',
  mixins: [
    methods
  ],
  data () {
    return {
      src: null,
      files: [],
      fileTooBig: false,
      fileNotAllowed: false,
      previewNotAvailable: true,
      id: `upload-${this.$uuidv4()}`,
      baseCss: this.componentBaseCss('upload', []),
      sizes: [
        'Bytes',
        'KB',
        'MB',
        'GB',
        'TB',
        'PB',
        'EB',
        'ZB',
        'YB'
      ]
    }
  },
  props: {
    mimeTypes: {
      type: Array,
      default: () => {
        return []
      }
    },
    block: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean
    },
    flat: {
      type: Boolean
    },
    rounded: {
      type: Boolean
    },
    plain: {
      type: Boolean
    },
    multiple: {
      type: Boolean
    },
    fancy: {
      type: Boolean
    },
    bgColor: {
      type: String
    },
    bgColorLightness: {
      type: Number
    },
    color: {
      type: String
    },
    colorLightness: {
      type: Number
    },
    width: {
      type: String
    },
    size: {
      type: String
    },
    iconLeft: {
      type: String
    },
    iconRight: {
      type: String
    },
    iconRemoveFile: {
      type: String,
      default: 'delete'
    },
    elevation: {
      type: String,
      default: 'md'
    },
    iconProvider: {
      type: String,
      default: 'mdi'
    },
    loading: {
      type: Boolean
    },
    maxFileSize: {
      type: Number
    },
    preview: {
      type: Boolean,
      default: true
    },
    messageFileTooBig: {
      type: String,
      default: 'File too big. Choose a smaller file'
    },
    messageFileNotAllowed: {
      type: String,
      default: 'The selected file type is not allowed for upload. Please, choose a supported file type'
    },
    messagePreviewNotAvailable: {
      type: String,
      default: 'Preview is not available for the selected file type'
    },
    label: {
      type: String,
      default: 'Choose File'
    },
    previewMaxHeight: {
      type: String
    }
  },
  methods: {
    format (bytes, decimals = 2) {
      if (bytes === 0) {
        return '0 Bytes'
      }
      const k = 1024
      const dm = decimals < 0 ? 0 : decimals
      const sizes = this.sizes
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
    },
    getFileList () {
      return this.$el.querySelector(`#${this.id}`).files
    },
    isImage (index) {
      return this.files.item(index).type.includes('image')
    },
    isText (index) {
      return this.files.item(index).type.includes('text')
    },
    getFileData () {
      this.files = this.getFileList()
      this.getBase64()
    },
    reset () {
      this.src = null
      this.files = []
      this.fileTooBig = false
      this.fileNotAllowed = false
      this.$el.querySelector(`form`).reset()
      this.emit('reset', new DataTransfer().files)
    },
    removeFile (index) {
      const list = new DataTransfer()
      const newFileList = Array.from(this.files)
      newFileList.splice(index, 1)
      newFileList.forEach(file => {
        list.items.add(file)
      })
      this.files = list.files
      this.emit()
    },
    getBase64 () {
      Array.from(this.files).forEach((file, index) => {
        const reader = new FileReader()
        const type = this.files.item(index).type
        if (this.mimeTypes.length && !this.mimeTypes.includes(type)) {
          this.fileNotAllowed = true
          return
        }
        if (this.isText(index)) {
          reader.readAsText(this.files.item(index))
        } else {
          reader.readAsDataURL(this.files.item(index))
        }
        reader.onload = () => {
          const size = this.files.item(index).size
          const maxFileSize = this.maxFileSize
          this.files.item(index).result = reader.result
          this.files.item(index).sizeFormatted = this.format(size)
          this.src = reader.result
          this.fileTooBig = false
          this.fileNotAllowed = false
          if (size > maxFileSize && !this.multiple) {
            this.reset()
            this.fileTooBig = true
            this.error = true
          } else if (size > maxFileSize && this.multiple) {
            this.files.item(index).fileTooBig = true
          }
        }
        reader.onerror = (error) => {
          console.error(error)
        }
      })
      this.emit()
    },
    emit (event = 'change', data = this.files) {
      this.$emit(event, data)
    }
  }
}
</script>
<style>
.bcq-upload input
{
  display: none;
}

.bcq-upload .bcq-upload-single-preview .bcq-upload-single-preview-details
{
  background: var(--semi-transparent-dark);
}
</style>



// WEBPACK FOOTER //
// src/bcq-ui/components/upload/upload.vue